import {
  isIdentifierRecalled,
  type DraftTargetLink,
  type IdentifierRecord,
} from '@myplayer/schema';
import { IconClock, IconEdit } from '@myplayer/ui';
import { useEffect, useState, type FormEvent, type ReactElement } from 'react';
import {
  ActionOverflowMenu,
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardIcon,
  CardRow,
  CardTitle,
  Input,
  ModalDialog,
  Select,
  Tag,
} from './components/ui/primitives.js';
import {
  cancelProductScheduledChangeAction,
  saveProductScheduledChangeAction,
  type ScheduleActionParams,
} from './product-qr-card.js';
import {
  formatPlainLinkTypeLabel,
  resolveAppliedScheduledChange,
  resolvePendingFiredScheduledChange,
  resolveScheduledChange,
} from './products-helpers.js';
import { resolveExampleBrandHostname } from './use-workspace-identifiers.js';

export {
  cancelProductScheduledChangeAction,
  saveProductScheduledChangeAction,
  type ScheduleActionParams,
};

export interface ProductScheduleSectionProps {
  readonly selectedRecord: IdentifierRecord;
  readonly workingLinks: readonly DraftTargetLink[];
  readonly canEdit: boolean;
  readonly isScheduleModalOpen: boolean;
  readonly editScheduledDate: string;
  readonly setEditScheduledDate: (val: string) => void;
  readonly editScheduledUrl: string;
  readonly setEditScheduledUrl: (val: string) => void;
  readonly savingDraft: boolean;
  readonly linkFormError: string | null;
  readonly effectiveResolverHost?: string;
  readonly onOpenScheduleModal: () => void;
  readonly onCloseScheduleModal: () => void;
  readonly onSaveSchedule: (e: FormEvent) => Promise<void>;
  readonly onClearSchedule: () => Promise<void>;
}

export function ProductScheduleSection(p: ProductScheduleSectionProps): ReactElement {
  const recalled = isIdentifierRecalled(p.selectedRecord);
  const scheduled = resolveScheduledChange(p.selectedRecord);
  const pendingFired = resolvePendingFiredScheduledChange(p.selectedRecord);
  const appliedSchedule = resolveAppliedScheduledChange(p.selectedRecord);
  const activeLinks = p.workingLinks.filter((l) => l.removed !== true);
  const matchedScheduledLink = scheduled
    ? activeLinks.find((l) => l.href === scheduled.targetHref)
    : undefined;
  const matchedModalLink = activeLinks.find((l) => l.href === p.editScheduledUrl);
  const [forceCustomUrl, setForceCustomUrl] = useState(() => !matchedModalLink);
  useEffect(() => {
    if (p.isScheduleModalOpen)
      setForceCustomUrl(!activeLinks.some((l) => l.href === p.editScheduledUrl));
  }, [p.isScheduleModalOpen]);
  const showCustomUrlInput = activeLinks.length === 0 || forceCustomUrl || !matchedModalLink;
  const hostForPlaceholder = resolveExampleBrandHostname(p.effectiveResolverHost);

  return (
    <>
      <Card data-testid="product-schedule-card">
        <CardHeader>
          <div className="flex min-w-0 items-center gap-3">
            <CardIcon>
              <IconClock />
            </CardIcon>
            <div className="min-w-0">
              <CardTitle>Scheduled change</CardTitle>
              <CardDescription>
                Automatically switch where scanning goes on a future date
              </CardDescription>
            </div>
          </div>
          {p.canEdit && (
            <div className="flex shrink-0 flex-wrap items-center gap-1.5">
              <Button
                type="button"
                size="sm"
                variant="secondary"
                data-testid="edit-schedule-button"
                onClick={p.onOpenScheduleModal}
              >
                <IconEdit />
                <span>{scheduled ? 'Edit schedule' : 'Schedule change'}</span>
              </Button>
              {scheduled && !recalled && (
                <ActionOverflowMenu
                  label="More actions for scheduled change"
                  disabled={p.savingDraft}
                  data-testid="schedule-overflow-menu"
                >
                  <Button
                    type="button"
                    size="sm"
                    variant="destructive"
                    data-testid="cancel-scheduled-change-button"
                    disabled={p.savingDraft}
                    onClick={() => void p.onClearSchedule()}
                  >
                    <span>Cancel scheduled change</span>
                  </Button>
                </ActionOverflowMenu>
              )}
            </div>
          )}
        </CardHeader>
        <CardContent>
          <div className="divide-y divide-subtle border-y border-subtle">
            {pendingFired && !recalled ? (
              <>
                <CardRow label="Status">
                  <Badge variant="warning" data-testid="schedule-switching-badge">
                    Switching now…
                  </Badge>
                </CardRow>
                <CardRow label="Changes to">
                  <div className="flex flex-col items-end gap-1 text-right">
                    <span
                      data-testid="scheduled-change-switching-banner"
                      className="text-[12px] font-medium text-fg"
                    >
                      Switching now to <span className="font-mono">{pendingFired.targetHref}</span>
                    </span>
                  </div>
                </CardRow>
              </>
            ) : scheduled && !recalled ? (
              <>
                <CardRow label="Status">
                  <Badge variant="warning">Scheduled</Badge>
                </CardRow>
                <CardRow label="Changes to">
                  <div className="flex flex-col items-end gap-1 text-right">
                    {matchedScheduledLink && (
                      <div className="flex flex-wrap items-center justify-end gap-1.5">
                        <span
                          data-testid="scheduled-change-link-title"
                          className="text-[12px] font-semibold text-fg"
                        >
                          {matchedScheduledLink.title}
                        </span>
                        <Tag variant="outline">
                          {formatPlainLinkTypeLabel(matchedScheduledLink.linkType)}
                        </Tag>
                      </div>
                    )}
                    <span
                      data-testid="scheduled-change-banner"
                      className="text-[12px] font-medium text-fg"
                    >
                      Changes to <span className="font-mono">{scheduled.targetHref}</span> on{' '}
                      {scheduled.formattedDate}
                    </span>
                  </div>
                </CardRow>
              </>
            ) : appliedSchedule && !recalled ? (
              <>
                <CardRow label="Status">
                  <Badge variant="valid" data-testid="schedule-applied-badge">
                    Applied
                  </Badge>
                </CardRow>
                <CardRow label="Applied">
                  <span
                    data-testid="scheduled-change-applied-banner"
                    className="text-[12px] font-medium text-fg"
                  >
                    Applied on {appliedSchedule.formattedDate} ·{' '}
                    <span className="font-mono">{appliedSchedule.targetHref}</span>
                  </span>
                </CardRow>
              </>
            ) : (
              <CardRow label="Schedule status">
                <span className="text-[12px] font-medium text-fg-secondary">
                  No scheduled change
                </span>
              </CardRow>
            )}
          </div>
        </CardContent>
      </Card>
      <ModalDialog
        open={p.isScheduleModalOpen}
        presentation="center"
        title="Schedule link change"
        subtitle={`GTIN ${p.selectedRecord.primaryKey}`}
        icon={<IconClock />}
        maxWidthClassName="max-w-2xl"
        submitting={p.savingDraft}
        onClose={p.onCloseScheduleModal}
        data-testid="schedule-editor-modal"
        footer={
          <>
            {scheduled && (
              <Button
                type="button"
                variant="destructive"
                size="sm"
                data-testid="clear-schedule-button"
                disabled={p.savingDraft}
                onClick={() => void p.onClearSchedule()}
              >
                Remove schedule
              </Button>
            )}
            <Button
              type="button"
              variant="secondary"
              size="sm"
              disabled={p.savingDraft}
              onClick={p.onCloseScheduleModal}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              form="product-schedule-edit-form"
              variant="primary"
              size="sm"
              data-testid="save-schedule-button"
              disabled={p.savingDraft}
            >
              {p.savingDraft ? 'Saving...' : 'Save'}
            </Button>
          </>
        }
      >
        <form
          id="product-schedule-edit-form"
          onSubmit={(e) => void p.onSaveSchedule(e)}
          className="space-y-4"
        >
          {activeLinks.length > 0 && (
            <div className="space-y-1.5">
              <label
                htmlFor="schedule-link-picker"
                className="block text-[13px] font-semibold text-fg"
              >
                Scheduled destination link
              </label>
              <Select
                id="schedule-link-picker"
                data-testid="schedule-link-picker"
                value={
                  !showCustomUrlInput && matchedModalLink ? matchedModalLink.href : '__CUSTOM__'
                }
                onChange={(e) => {
                  const val = e.target.value;
                  setForceCustomUrl(val === '__CUSTOM__');
                  if (val !== '__CUSTOM__' && val.length > 0) p.setEditScheduledUrl(val);
                }}
              >
                {activeLinks.map((l, idx) => (
                  <option key={`${l.href}-${idx}`} value={l.href}>
                    {l.title} ({formatPlainLinkTypeLabel(l.linkType)})
                  </option>
                ))}
                <option value="__CUSTOM__">Enter a custom HTTPS web address…</option>
              </Select>
              {!showCustomUrlInput && matchedModalLink && (
                <div
                  data-testid="selected-schedule-link-preview"
                  className="flex min-w-0 items-center gap-2 rounded-control border border-subtle bg-surface-muted px-3 py-2 text-[12px]"
                >
                  <Tag variant="outline">{formatPlainLinkTypeLabel(matchedModalLink.linkType)}</Tag>
                  <span className="truncate font-mono text-fg-secondary">
                    {matchedModalLink.href}
                  </span>
                </div>
              )}
            </div>
          )}
          {showCustomUrlInput && (
            <div className="space-y-1.5">
              <label
                htmlFor="schedule-url-input"
                className="block text-[12px] font-medium text-fg-secondary"
              >
                New web address on that date
              </label>
              <Input
                id="schedule-url-input"
                data-testid="schedule-url-input"
                value={p.editScheduledUrl}
                onChange={(e) => p.setEditScheduledUrl(e.target.value)}
                placeholder={`https://${hostForPlaceholder}/campaign`}
              />
            </div>
          )}
          <div className="space-y-1.5">
            <label
              htmlFor="schedule-date-input"
              className="block text-[12px] font-medium text-fg-secondary"
            >
              Switch date and time
            </label>
            <Input
              id="schedule-date-input"
              data-testid="schedule-date-input"
              type="datetime-local"
              value={p.editScheduledDate}
              onChange={(e) => p.setEditScheduledDate(e.target.value)}
            />
          </div>
          {p.linkFormError && (
            <p
              role="alert"
              data-testid="link-editor-error"
              className="rounded-control border border-status-error-border bg-status-error-bg p-2.5 text-[12px] font-medium text-status-error"
            >
              {p.linkFormError}
            </p>
          )}
        </form>
      </ModalDialog>
    </>
  );
}
